import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { BillingPeriodToggle } from '@/features/subscriptions/components/billing-period-toggle';
import { PricingCard } from '@/features/subscriptions/components/pricing-card';
import { addonsPlans } from '@/features/subscriptions/data/addons-plans';
import { organizationPlans } from '@/features/subscriptions/data/organization-plans';
import { projectPlans } from '@/features/subscriptions/data/project-plans';
import { useCanManageSubscriptions } from '@/features/subscriptions/hooks/use-can-manage-subscriptions';
import { usePricing } from '@/features/subscriptions/hooks/use-pricing';
import {
  BillingPeriod,
  PlanData,
} from '@/features/subscriptions/types/subscription-types';
import SyncaraLogo from '@/shared/assets/syncara_logo.svg?react';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';
import { cn } from '@/shared/utils/cn';
import { useErrorTranslation } from '@/shared/utils/error-handling';

export const PlansManagementPage = () => {
  const { t } = useTranslation('subscriptions');
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'org' | 'project'>('org');
  const canManageSubscriptions = useCanManageSubscriptions();

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'project' || tabParam === 'org') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  if (!canManageSubscriptions) {
    return <Navigate to={paths.dashboard.path} replace />;
  }

  return (
    <DefaultLayout>
      <div className="flex flex-col">
        <div
          className={cn(
            'flex flex-col rounded-5xl border border-dashed border-white/15 p-8 shadow-inner',
            Gradients.transparentWhiteGradient,
          )}
        >
          <div className="flex w-full flex-col gap-5">
            <div
              className="flex w-fit flex-row gap-1 rounded-full border py-1.5 pl-2 pr-3"
              style={{
                backgroundColor: 'hsla(271, 91%, 65%, 0.1)',
                borderColor: 'hsla(271, 91%, 65%, 0.2)',
              }}
            >
              <SyncaraLogo className="size-5" />
              <span
                className="text-xs"
                style={{ color: 'hsla(270, 95%, 75%, 1)' }}
              >
                {t('PlansManagementPage.capsule_text')}
              </span>
            </div>
            <span className="text-4xl font-medium">
              {t('PlansManagementPage.title1')}{' '}
              <span
                className={cn(
                  Gradients.purpleBlueGradient,
                  'bg-gradient-to-b bg-clip-text text-transparent',
                )}
              >
                {t('PlansManagementPage.title2')}
              </span>{' '}
              {t('PlansManagementPage.title3')}
            </span>
            <span className="text-sm text-white/60 md:w-1/2">
              {t('PlansManagementPage.description')}
            </span>
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as 'org' | 'project')
              }
            >
              <TabsList className="mb-16 flex h-[52px] w-full flex-row justify-start rounded-xl border-none bg-dark-container p-2">
                <TabsTrigger
                  value="org"
                  className={'flex-1 truncate rounded-md px-3'}
                >
                  <span className="truncate">
                    {t('PlansManagementPage.organization_tab')}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="project"
                  className={'flex-1 truncate rounded-md px-3'}
                >
                  <span className="truncate">
                    {t('PlansManagementPage.project_tab')}
                  </span>
                </TabsTrigger>
              </TabsList>

              <OrganizationPlans />
              <ProjectPlans />
            </Tabs>
          </div>
        </div>
        <div className="mb-8 flex flex-col items-center gap-6 pt-8 text-center">
          <p className="text-xs font-semibold text-custom-grey">
            {t('organizationPlans.contactSales')}
          </p>
          <Button variant="outline" className="min-w-60">
            {t('buttons.contactSales')}
          </Button>
        </div>
      </div>
    </DefaultLayout>
  );
};

export const OrganizationPlans = () => {
  const { t } = useTranslation('subscriptions');

  return (
    <TabsContent value="org">
      <PlanTemplate
        title={t('PlansManagementPage.OrganizationTab.title')}
        description={t('PlansManagementPage.OrganizationTab.description')}
        plans={organizationPlans}
      />
    </TabsContent>
  );
};

export const ProjectPlans = () => {
  const { t } = useTranslation('subscriptions');
  return (
    <TabsContent value="project">
      <div className="flex flex-col gap-16">
        <PlanTemplate
          title={t('PlansManagementPage.ProjectTab.title')}
          description={t('PlansManagementPage.ProjectTab.description')}
          plans={projectPlans}
        />
        <PlanTemplate
          title={t('PlansManagementPage.ProjectTab.addons_title')}
          description={t('PlansManagementPage.ProjectTab.addons_description')}
          plans={addonsPlans}
        />
      </div>
    </TabsContent>
  );
};

const PlanTemplate = ({
  title,
  description,
  plans,
}: {
  title: string;
  description: string;
  plans: PlanData[];
}) => {
  const { t } = useTranslation('subscriptions');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const { refetch, error: pricingError } = usePricing();
  const getErrorTranslation = useErrorTranslation();

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-row gap-2">
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-2xl font-semibold">{title}</span>
          <span className="text-sm text-white/60">{description}</span>
        </div>

        <BillingPeriodToggle
          billingPeriod={billingPeriod}
          onBillingPeriodChange={setBillingPeriod}
        />
      </div>

      <div className="relative h-full">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
            />
          ))}
        </div>
        {pricingError && (
          <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-8 rounded-md border bg-dark-container p-8">
            <div className="flex flex-col items-center gap-2">
              <span>{t('PlansManagementPage.failedToLoadPlans')}</span>
              <span>{getErrorTranslation(pricingError)}</span>
            </div>
            <Button
              variant="outline"
              className="min-w-60"
              onClick={() => refetch()}
            >
              {t('PlansManagementPage.tryAgainButton')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
